import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatButtonModule } from '@angular/material/button'
import { MatIconModule } from '@angular/material/icon'
import { MatTooltipModule } from '@angular/material/tooltip'
import { TranslatePipe, TranslateService } from '@ngx-translate/core'
import { FindReplaceTarget } from './find-replace-target'

@Component({
  selector: 'app-find-replace-panel',
  standalone: true,
  imports: [FormsModule, MatButtonModule, MatIconModule, MatTooltipModule, TranslatePipe],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './find-replace-panel.component.html',
  styleUrl: './find-replace-panel.component.scss',
})
export class FindReplacePanelComponent implements OnInit, AfterViewInit, OnDestroy {
  /** What is searched: the minutes editor, or a Restricted user's sections searched as one. */
  @Input({ required: true })
  target!: FindReplaceTarget

  @Input()
  canUserEdit = false

  @Output()
  closed = new EventEmitter<void>()

  @Output()
  replaceRequested = new EventEmitter<void>()

  @Output()
  replaceAllRequested = new EventEmitter<void>()

  @ViewChild('searchInput')
  searchInputRef?: ElementRef<HTMLInputElement>

  private translateService = inject(TranslateService)
  private changeDetectorRef = inject(ChangeDetectorRef)

  searchTerm = ''
  replaceTerm = ''
  resultCount = 0
  currentIndex: number | null = null

  private unsubscribe: (() => void) | null = null

  ngOnInit() {
    const state = this.target.readState()
    this.searchTerm = state.searchTerm
    this.replaceTerm = state.replaceTerm
    this.syncFromTarget()
    this.unsubscribe = this.target.subscribe(() => {
      this.syncFromTarget()
      this.changeDetectorRef.markForCheck()
    })
  }

  ngAfterViewInit() {
    this.focusSearchInput()
  }

  focusSearchInput() {
    this.searchInputRef?.nativeElement.focus()
    this.searchInputRef?.nativeElement.select()
  }

  ngOnDestroy() {
    this.unsubscribe?.()
  }

  get resultCountLabel(): string {
    if (this.searchTerm.length === 0) {
      return ''
    }
    if (this.resultCount === 0) {
      return this.translateService.instant('EDITOR.FIND_REPLACE.NO_RESULTS')
    }
    const current = this.currentIndex !== null ? this.currentIndex + 1 : 0
    return this.translateService.instant('EDITOR.FIND_REPLACE.RESULT_COUNT', { current, total: this.resultCount })
  }

  onSearchTermChange(term: string) {
    this.target.setSearchTerm(term)
  }

  onReplaceTermChange(term: string) {
    this.target.setReplaceTerm(term)
  }

  onSearchInputEnter(event: Event) {
    event.preventDefault()
    if ((event as KeyboardEvent).shiftKey) {
      this.goToPrevious()
    } else {
      this.goToNext()
    }
  }

  goToNext() {
    this.target.goToNext()
  }

  goToPrevious() {
    this.target.goToPrevious()
  }

  replace() {
    this.replaceRequested.emit()
  }

  replaceAll() {
    this.replaceAllRequested.emit()
    // A tracked replace-all clears the underlying search (see find-replace-track-changes.ts), which the
    // listener above only picks up for resultCount/currentIndex — searchTerm needs the same refresh so the
    // input doesn't keep showing a term whose search was just cleared out from under it.
    this.searchTerm = this.target.readState().searchTerm
  }

  onClose() {
    this.target.clearSearch()
    this.closed.emit()
  }

  private syncFromTarget() {
    const state = this.target.readState()
    const previousIndex = this.currentIndex
    this.resultCount = state.resultCount
    this.currentIndex = state.currentIndex
    if (this.currentIndex !== null && this.currentIndex !== previousIndex) {
      this.target.revealCurrent()
    }
  }
}
